import React, { useEffect, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { ProjectFileMetadata } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { downloadProjectFiles } from '../../store/actions/download';
import { useAppDispatch } from '../hooks';

export interface ProjectFilesDialogProps {
  readonly projectGuid: string;
}

export function ProjectFilesDialog({ projectGuid }: ProjectFilesDialogProps) {
  const dispatch = useAppDispatch();

  const [projectFilesMetadata, setProjectFilesMetadata] = useState<
    readonly ProjectFileMetadata[] | 'loading' | 'error'
  >('loading');
  const [selection, setSelection] = useState<readonly string[]>([]);

  const columns: GridColDef<ProjectFileMetadata>[] = [
    { field: 'fileName', headerName: 'File Name', width: 400 },
    {
      field: 'added',
      headerName: 'Added',
      width: 150,
      valueGetter: (value) =>
        Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)),
    },
    { field: 'addedByEmail', headerName: 'Added By', width: 200 },
  ];

  useEffect(() => {
    designService.projectFiles
      .getProjectFilesMetadata(projectGuid)
      .then((projectFilesMetadata) => {
        setProjectFilesMetadata(projectFilesMetadata);
      })
      .catch((error) => {
        console.error(error);
        setProjectFilesMetadata('error');
      });
    return () => setProjectFilesMetadata('loading');
  }, [projectGuid]);

  return (
    <Dialog
      title="Project Files"
      submitText="download"
      onSubmit={() => {
        dispatch(downloadProjectFiles(projectGuid, selection));
        dispatch(closeCurrentPopup());
      }}
      submitDisabled={selection.length === 0}
      cancelText="close"
      onCancel="close"
    >
      {projectFilesMetadata === 'loading' ? (
        <CircularProgress />
      ) : projectFilesMetadata === 'error' ? (
        <Alert severity="error">Error loading files</Alert>
      ) : projectFilesMetadata.length === 0 ? (
        <Alert severity="info">No project files exist.</Alert>
      ) : (
        <DataGrid
          rows={projectFilesMetadata}
          getRowId={(row) => row.guid}
          columns={columns}
          checkboxSelection
          onRowSelectionModelChange={(selectionModel) => {
            if (selectionModel.type === 'include') {
              setSelection(Array.from(selectionModel.ids) as readonly string[]);
            } else if (selectionModel.type === 'exclude') {
              setSelection(projectFilesMetadata.map((row) => row.guid).filter((id) => !selectionModel.ids.has(id)));
            } else {
              assertExhaustiveSwitch(selectionModel.type);
            }
          }}
          initialState={{ sorting: { sortModel: [{ field: 'added', sort: 'desc' }] } }}
        />
      )}
    </Dialog>
  );
}
